/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  IconButton,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Stack,
  Tabs,
  Tab,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BatchPredictionIcon from '@mui/icons-material/BatchPrediction';
import { toast } from 'react-hot-toast';
import GenerateNewInvoice from './GenerateNewInvoice';
import EditInvoice from './EditInvoice';
import RecordPayment from './RecordPayment';
import EditPayment from './EditPayment';
import InvoiceDetail from './InvoiceDetail';
import '../../styles/ManageProgramData.css';
import BatchInvoicing from './BatchInvoicing';
import FinalizeInvoiceDialog from './FinalizeInvoiceDialog';
import useFacility from '../../hooks/useFacility';

// Helper function to extract invoice number from invoice_id
// e.g., "202601270728_INV011" -> 11
const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = invoiceId.split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

// The invoicing screen is organised by where an invoice sits in its lifecycle
// rather than by project. 'not_invoiced' is the only project-level tab — it
// lists projects that have no invoice at all — every other tab lists invoices.
// Project statuses that can still be invoiced. This used to be a filter on the
// screen, but invoicing always wants the same set, so it is fixed here.
const INVOICEABLE_PROJECT_STATUSES = ['Approved', 'In Progress', 'Requires Extension', 'Completed'];

const BRAND_RED = 'var(--brand-primary, #c41e3a)';

// Enough contrast that one invoice visibly ends before the next begins.
const INVOICE_CARD_SX = {
  borderColor: '#cbd5e1',
  borderRadius: 2,
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.10)'
};

const CARD_HEADER_SX = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 1,
  flexWrap: 'wrap',
  px: 2,
  py: 1.25,
  backgroundColor: '#f1f5f9',
  borderBottom: '1px solid #e2e8f0'
};

const STATUS_TABS = [
  {
    key: 'not_invoiced',
    label: 'Not Invoiced',
    unit: 'projects',
    help: 'Projects with compartment usage nobody has invoiced yet. Generate an invoice to bill the outstanding days.'
  },
  {
    key: 'invoiced',
    label: 'Invoiced',
    unit: 'invoices',
    help: 'Created but not sent yet. Only admins can see these — edit freely, then Finalize & Send.'
  },
  {
    key: 'billed',
    label: 'Billed',
    unit: 'invoices',
    help: 'Sent to the project team. The PI sets the payment split and payers sign.'
  },
  {
    key: 'dispute',
    label: 'Dispute',
    unit: 'invoices',
    help: 'A PI or Co-PI raised a problem. Signing is paused until you revise the invoice and resolve the dispute.'
  },
  {
    key: 'signed',
    label: 'Signed',
    unit: 'invoices',
    help: 'All designated payers signed. Outstanding balance is now collectible.'
  },
  {
    key: 'paid',
    label: 'Paid',
    unit: 'invoices',
    help: 'Balance settled in full.'
  }
];

const Invoicing = () => {
  // Get admin's facility preference
  const { facilityPreference, loading: prefLoading } = useFacility();
  const location = useLocation();
  // Deep link from an invoice notification:
  // ?search=<project>&invoiceId=<id>&tab=<lifecycle tab>. Consumed once per
  // invoice so the admin can then search and switch tabs freely.
  const deepLinkHandledRef = useRef(null);
  
  const [projects, setProjects] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [projectInvoices, setProjectInvoices] = useState({});
  const [selectedProject, setSelectedProject] = useState(null);
  const [generateInvoiceDialogOpen, setGenerateInvoiceDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [finalizeDialogOpen, setFinalizeDialogOpen] = useState(false);
  const [invoiceToFinalize, setInvoiceToFinalize] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filter states
  const [selectedFacility, setSelectedFacility] = useState('all');
  
  // Set facility filter from admin preference when loaded
  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  // Which lifecycle tab is showing. Starts on Billed because that is where
  // invoices sit while they need chasing; Not Invoiced is a generation queue.
  const [activeTab, setActiveTab] = useState('billed');

  // Projects that still have chamber days nobody has invoiced, keyed by project id.
  const [unbilledByProject, setUnbilledByProject] = useState({});

  // Pagination states. All projects are loaded up-front and filtering/search
  // run across the full set; pagination only controls how many of the already
  // filtered results are rendered at a time.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [invoices, setInvoices] = useState([]);
  const [showBatchInvoice, setShowBatchInvoice] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);

  // Delete invoice states
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit invoice states
  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [projectForEdit, setProjectForEdit] = useState(null);

  // Unified payments dialog states
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [paymentsInvoice, setPaymentsInvoice] = useState(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [deletePaymentConfirmOpen, setDeletePaymentConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);

  // Resolve-dispute dialog states
  const [resolveDisputeOpen, setResolveDisputeOpen] = useState(false);
  const [disputeToResolve, setDisputeToResolve] = useState(null);
  const [resolutionNote, setResolutionNote] = useState('');
  const [isResolvingDispute, setIsResolvingDispute] = useState(false);

  // Account mapping dialog states
  const [accountMappingDialogOpen, setAccountMappingDialogOpen] = useState(false);
  const [invoiceForMapping, setInvoiceForMapping] = useState(null);
  const [projectForMapping, setProjectForMapping] = useState(null);
  const [selectedMappingFacility, setSelectedMappingFacility] = useState('');
  const [selectedFacilityAccounts, setSelectedFacilityAccounts] = useState([]);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState('');

  useEffect(() => {
    fetchFacilities();
  }, []);

  const buildProjectParams = () => {
    const params = new URLSearchParams();
    params.append('application_type', 'project');
    // Fetch all matching projects. The endpoint defaults to 50 with no
    // pagination UI here, which silently hid ended/older projects at
    // quarter-end invoicing time.
    params.append('limit', '10000');
    if (selectedFacility !== 'all') {
      params.append('facility', selectedFacility);
    }
    params.append('status', INVOICEABLE_PROJECT_STATUSES.join(','));
    return params;
  };

  const fetchProjectList = async () => {
    const response = await axios.get(`/api/admin/projects?${buildProjectParams().toString()}`);
    const projectsData = response.data.projects || response.data || [];
    return Array.isArray(projectsData) ? projectsData : [];
  };

  // Outstanding compartment days per project, used by the Not Invoiced tab.
  // A failure here should not take the whole page down, so it degrades to an
  // empty map and the tab simply shows nothing to invoice.
  const fetchUnbilledSummary = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedFacility !== 'all') params.append('facility', selectedFacility);
      params.append('status', INVOICEABLE_PROJECT_STATUSES.join(','));
      const response = await axios.get(`/api/invoices/unbilled-summary?${params.toString()}`);
      const rows = response.data?.projects || [];
      return rows.reduce((acc, row) => {
        acc[String(row.project)] = row;
        return acc;
      }, {});
    } catch (err) {
      console.error('Failed to load unbilled usage summary:', err?.response?.data || err?.message);
      return {};
    }
  };

  // Main data fetch. Inlined into the effect so we can use a cancellation
  // flag to ignore stale responses. Without this, two concurrent fetches
  // (e.g. an initial `selectedFacility='all'` fetch racing the facility-
  // preference-driven refetch) would clobber each other and cause a
  // "Paid invoice flashes, then vanishes into 'Not Invoiced'" UX bug.
  // Also waits for the facility preference to finish loading before the
  // first fetch, so we don't issue a throwaway request.
  useEffect(() => {
    if (prefLoading) return undefined;

    let cancelled = false;

    (async () => {
      try {
        setLoading(true);

        const [projectsArr, unbilledMap] = await Promise.all([
          fetchProjectList(),
          fetchUnbilledSummary()
        ]);
        if (cancelled) return;

        const invoicesData = {};
        for (const project of projectsArr) {
          if (cancelled) return;
          if (!project._id) continue;
          try {
            const invoicesResponse = await axios.get(`/api/invoices/by-project/${project._id}`);
            if (cancelled) return;
            invoicesData[project._id] = invoicesResponse.data || [];
          } catch (err) {
            // Make silent failures visible so we can diagnose instead of
            // falsely showing "Not Invoiced" when the invoice fetch blew up.
            console.error(
              `Failed to load invoices for project ${project.project_id || project._id}:`,
              err?.response?.status,
              err?.response?.data || err?.message
            );
            invoicesData[project._id] = [];
          }
        }

        if (cancelled) return;
        // Commit projects and their invoices TOGETHER so the UI never
        // renders with the new projects but stale/empty invoice data.
        setProjects(projectsArr);
        setProjectInvoices(invoicesData);
        setUnbilledByProject(unbilledMap);
        setError(null);
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch projects:', error?.response?.data || error?.message);
        setError('Failed to fetch projects');
        toast.error('Failed to fetch projects');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // Only the facility is sent to the server. The date range, search and
    // lifecycle tab are applied client-side, so refetching on them would re-run
    // the whole per-project invoice load for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefLoading, selectedFacility]);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get('/api/facilities');
      setFacilities(response.data);
    } catch (error) {
      setError('Failed to fetch facilities');
      toast.error('Failed to fetch facilities');
    }
  };

  // Imperative refetch used after actions (invoice generated/deleted/edited,
  // account mapping changed, etc.). Commits projects + projectInvoices
  // together to avoid the intermediate "new projects with stale/empty
  // invoices" render that previously caused a "Paid -> Not Invoiced" flash.
  const fetchProjects = async () => {
    try {
      setLoading(true);

      const [projectsArr, unbilledMap] = await Promise.all([
        fetchProjectList(),
        fetchUnbilledSummary()
      ]);

      const invoicesData = {};
      for (const project of projectsArr) {
        if (!project._id) continue;
        try {
          const invoicesResponse = await axios.get(`/api/invoices/by-project/${project._id}`);
          invoicesData[project._id] = invoicesResponse.data || [];
        } catch (err) {
          console.error(
            `Failed to load invoices for project ${project.project_id || project._id}:`,
            err?.response?.status,
            err?.response?.data || err?.message
          );
          invoicesData[project._id] = [];
        }
      }

      setProjects(projectsArr);
      setProjectInvoices(invoicesData);
      setUnbilledByProject(unbilledMap);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch projects:', error?.response?.data || error?.message);
      setError('Failed to fetch projects');
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  // Populates the flat `invoices` state used by a few imperative flows
  // (e.g. full-list refresh after deletions). The per-project list that
  // actually drives the Invoicing tab UI is `projectInvoices`, which is
  // owned by the main effect/`fetchProjects`. This function deliberately
  // does NOT touch the shared `loading` flag, because that flag is
  // reserved for the project-fetch lifecycle; otherwise this fetch would
  // race with the main effect and flip `loading` to false before the
  // projects have arrived, causing a brief "No projects found" flash.
  const fetchInvoices = async () => {
    try {
      const response = await axios.get('/api/invoices');
      setInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch invoices list:', error?.response?.data || error?.message);
    }
  };

  // Handle project selection
  const handleProjectSelect = async (project) => {
    try {
      // Fetch detailed project data with populated fields
      const response = await axios.get(`/api/projects/${project._id}`);
      setSelectedProject(response.data);
      setGenerateInvoiceDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load project details');
      // Fall back to using the basic project data
      setSelectedProject(project);
      setGenerateInvoiceDialogOpen(true);
    }
  };

  // Handle invoice generation
  const handleInvoiceGenerated = (newInvoice) => {
    // Refresh projects list to show the new invoice
    fetchProjects();
    toast.success('Invoice generated successfully');
  };

  // Handle opening invoice details
  const handleViewInvoiceDetails = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetail(true);
  };

  // Open the unified Payments dialog
  const handleOpenPayments = async (invoice) => {
    try {
      const response = await axios.get(
        `/api/invoices/${invoice._id || invoice.parentId}?populate=true&invoice_id=${invoice.invoice_id}`
      );
      setPaymentsInvoice(response.data);
      setPaymentsDialogOpen(true);
    } catch (error) {
      console.error('Error fetching invoice for payments:', error);
      setPaymentsInvoice(invoice);
      setPaymentsDialogOpen(true);
    }
  };

  const handlePaymentRecorded = async () => {
    fetchInvoices();
    fetchProjects();
    setShowRecordPayment(false);
    // Re-fetch the invoice so the dialog shows updated payments
    if (paymentsInvoice) {
      try {
        const response = await axios.get(
          `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}?populate=true&invoice_id=${paymentsInvoice.invoice_id}`
        );
        setPaymentsInvoice(response.data);
      } catch (err) {
        console.error('Error refreshing invoice after payment:', err);
      }
    }
  };

  const handlePaymentUpdated = async () => {
    fetchProjects();
    fetchInvoices();
    setEditPaymentDialogOpen(false);
    setPaymentToEdit(null);
    if (paymentsInvoice) {
      try {
        const response = await axios.get(
          `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}?populate=true&invoice_id=${paymentsInvoice.invoice_id}`
        );
        setPaymentsInvoice(response.data);
      } catch (err) {
        console.error('Error refreshing invoice after payment edit:', err);
      }
    }
  };

  const handleConfirmDeletePayment = async () => {
    if (!paymentsInvoice || !paymentToDelete) return;
    setIsDeletingPayment(true);
    try {
      await axios.delete(
        `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}/payment/${paymentToDelete._id}?invoice_id=${paymentsInvoice.invoice_id}`
      );
      toast.success('Payment deleted successfully');
      setDeletePaymentConfirmOpen(false);
      setPaymentToDelete(null);
      fetchProjects();
      fetchInvoices();
      // Re-fetch the invoice so the dialog reflects the removed payment
      try {
        const response = await axios.get(
          `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}?populate=true&invoice_id=${paymentsInvoice.invoice_id}`
        );
        setPaymentsInvoice(response.data);
      } catch (err) {
        console.error('Error refreshing invoice after payment deletion:', err);
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeletingPayment(false);
    }
  };

  // Get account mapping display info
  const getAccountMappingInfo = (invoice) => {
    if (!invoice?.account_mapping?.facility_id || !invoice?.account_mapping?.account_number) {
      return null;
    }
    
    const facilityId = typeof invoice.account_mapping.facility_id === 'object' 
      ? invoice.account_mapping.facility_id._id || invoice.account_mapping.facility_id
      : invoice.account_mapping.facility_id;
    
    const facility = facilities.find(f => f._id === facilityId);
    if (!facility) return null;
    
    return {
      facilityName: facility.facility_name,
      accountNumber: invoice.account_mapping.account_number
    };
  };

  // Open account mapping dialog
  const handleOpenAccountMapping = (invoice, project) => {
    setInvoiceForMapping(invoice);
    setProjectForMapping(project);
    
    // If invoice already has mapping, pre-select it
    if (invoice?.account_mapping?.facility_id) {
      const facilityId = typeof invoice.account_mapping.facility_id === 'object'
        ? invoice.account_mapping.facility_id._id || invoice.account_mapping.facility_id
        : invoice.account_mapping.facility_id;
      
      setSelectedMappingFacility(facilityId);
      const facility = facilities.find(f => f._id === facilityId);
      if (facility) {
        setSelectedFacilityAccounts(facility.accounts_receivable || []);
        setSelectedAccountNumber(invoice.account_mapping.account_number || '');
      }
    } else {
      setSelectedMappingFacility('');
      setSelectedFacilityAccounts([]);
      setSelectedAccountNumber('');
    }
    
    setAccountMappingDialogOpen(true);
  };

  // Handle facility selection change in mapping dialog
  const handleMappingFacilityChange = (facilityId) => {
    setSelectedMappingFacility(facilityId);
    const facility = facilities.find(f => f._id === facilityId);
    if (facility) {
      setSelectedFacilityAccounts(facility.accounts_receivable || []);
    } else {
      setSelectedFacilityAccounts([]);
    }
    setSelectedAccountNumber('');
  };

  // Save account mapping
  const handleSaveAccountMapping = async () => {
    try {
      if (!invoiceForMapping || !projectForMapping) {
        toast.error('Missing invoice or project information');
        return;
      }

      const response = await axios.patch(
        `/api/invoices/${projectForMapping.project_id || projectForMapping._id}/${invoiceForMapping.invoice_id}/account-mapping`,
        {
          facility_id: selectedMappingFacility || null,
          account_number: selectedAccountNumber || null
        }
      );

      if (response.data.success) {
        toast.success('Account mapping updated successfully');
        fetchProjects(); // Refresh to show updated mapping
        fetchInvoices();
        setAccountMappingDialogOpen(false);
      } else {
        toast.error('Failed to update account mapping');
      }
    } catch (error) {
      console.error('Error updating account mapping:', error);
      toast.error('Failed to update account mapping');
    }
  };

  // Download PDF
  const handleDownloadPDF = async (invoiceId, invoice_id) => {
    try {
      // Ensure we have the invoice ID from the table row
      if (!invoice_id) {
        // Try to find the invoice in our data
        const invoice = Object.values(projectInvoices)
          .flat()
          .find(inv => inv._id === invoiceId || inv.parentId === invoiceId);
        
        if (invoice) {
          invoice_id = invoice.invoice_id;
        } else {
          toast.error('Missing invoice ID information');
          return;
        }
      }
      
      const response = await axios.get(`/api/invoices/${invoiceId}/pdf?invoice_id=${invoice_id}`, {
        responseType: 'blob'
      });
      
      // Extract filename from Content-Disposition header if available
      let filename = `invoice-${getInvoiceNumber(invoice_id)}.pdf`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(?:(?:"([^"]*)")|([^;\n]*))/i);
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          filename = filenameMatch[1] || filenameMatch[2];
        }
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  // Get the latest status of an invoice
  const getInvoiceStatus = (invoice) => {
    // Legacy invoices keep their old status; the new lifecycle does not apply.
    if (!invoice.is_legacy && invoice.workflow_status) {
      return invoice.workflow_status;
    }
    if (!invoice.status_history || invoice.status_history.length === 0) {
      return 'Unknown';
    }
    
    const sortedHistory = [...invoice.status_history].sort((a, b) => 
      new Date(b.date) - new Date(a.date)
    );
    
    return sortedHistory[0].status;
  };

  // Get status color for styling
  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid':
      case 'Signed':
        return 'success';
      case 'Partially Paid':
        return 'warning';
      case 'Overdue':
      case 'Disputed':
        return 'error';
      case 'Billed':
      case 'Sent to Client':
        return 'info';
      case 'Final':
        return 'primary';
      case 'Draft':
        return 'default';
      default:
        return 'default';
    }
  };

  // Is there nothing left to collect? Deliberately NOT true for a $0 invoice,
  // otherwise every empty draft would be counted as paid.
  const isBalanceSettled = (invoice) => {
    const total = invoice.total_amount || invoice.current_period_amount || 0;
    const paid = invoice.amount_paid || 0;
    const remaining = invoice.remaining_balance ?? (total - paid);
    if (remaining <= 0.005 && (paid > 0 || (invoice.payments && invoice.payments.length > 0))) {
      return true;
    }
    if (total > 0 && paid >= total - 0.005) return true;
    const history = invoice.status_history || [];
    if (history.length > 0) {
      const latest = [...history].sort((a, b) => new Date(b.date) - new Date(a.date))[0];
      if (latest?.status === 'Paid') return true;
    }
    return false;
  };

  // Which lifecycle tab an invoice belongs to, so every invoice appears under
  // exactly one tab. Only an invoice that has finished the lifecycle moves to
  // Paid: a Billed one stays put even if the balance is settled, because the
  // outstanding signatures are the thing the admin still has to chase. A dispute
  // stays under Dispute until it is resolved. Draft/Final (the Invoiced tab)
  // and legacy invoices move to Paid once a payment settles the balance.
  const getInvoiceBucket = (invoice) => {
    const settled = isBalanceSettled(invoice);
    // Legacy invoices predate the lifecycle, so balance is all we can go on.
    if (invoice.is_legacy) return settled ? 'paid' : 'invoiced';

    switch (invoice.workflow_status) {
      case 'Billed':
        return 'billed';
      case 'Disputed':
        return 'dispute';
      case 'Signed':
        return settled ? 'paid' : 'signed';
      default:
        return settled ? 'paid' : 'invoiced';
    }
  };

  // Format date properly handling timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone shift
    const month = date.getUTCMonth() + 1;
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  };

  // Filter projects based on search term and filters. This runs before the
  // lifecycle tabs, so search/facility/date narrow the pool that every tab draws
  // from — including the invoice rows, which inherit their project's match.
  const filteredProjects = projects.filter(project => {
    // Search across all users (not just PI)
    const allUserNames = project.users?.map(user => 
      `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase()
    ).join(' ') || '';
    
    const allUserEmails = project.users?.map(user => 
      (user.email || '').toLowerCase()
    ).join(' ') || '';
    
    const matchesSearch = searchTerm === '' || 
      project.project_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.project_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.status?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      allUserNames.includes(searchTerm.toLowerCase()) ||
      allUserEmails.includes(searchTerm.toLowerCase());

    const matchesFacility = selectedFacility === 'all' || project.facility?._id === selectedFacility;
    
    // Safe date parsing with null checks
    const projectStartDate = project.project_start_date ? new Date(project.project_start_date) : null;
    const projectEndDate = project.project_end_date ? new Date(project.project_end_date) : null;
    const matchesDateRange = (!startDate || !projectStartDate || isNaN(projectStartDate.getTime()) || projectStartDate >= startDate) && 
                           (!endDate || !projectEndDate || isNaN(projectEndDate.getTime()) || projectEndDate <= endDate);

    return matchesSearch && matchesFacility && matchesDateRange;
  });

  // Find PI - check multiple possible role formats. Falls back to the first user
  // so a project without an explicit PI still shows a contact.
  const getDisplayUser = (project) => {
    const pi = project.users?.find(user => {
      if (!user.role) return false;
      if (typeof user.role === 'object' && user.role !== null) {
        const roleName = user.role.role || user.role.name || '';
        return roleName === 'PI' || roleName === 'Principal Investigator';
      }
      return user.role === 'PI' || user.role === 'Principal Investigator';
    });
    return pi || project.users?.[0];
  };

  // The generation queue: projects that still have compartment days nobody has
  // invoiced. A project can already have invoices and still land here — that is
  // the point, since usage keeps accruing after the last bill went out.
  const notInvoicedProjects = useMemo(
    () => filteredProjects
      .map(p => ({ project: p, unbilled: unbilledByProject[String(p._id)] }))
      .filter(row => row.unbilled)
      .sort((a, b) => (b.unbilled.unbilled_days || 0) - (a.unbilled.unbilled_days || 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projects, unbilledByProject, searchTerm, selectedFacility, startDate, endDate]
  );

  // Every invoice of every matching project, flattened into rows that carry
  // their project along so a row can render and act on its own.
  const invoiceRows = useMemo(() => {
    const rows = [];
    for (const project of filteredProjects) {
      for (const invoice of projectInvoices[project._id] || []) {
        rows.push({ invoice, project, bucket: getInvoiceBucket(invoice) });
      }
    }
    // Newest billing period first: the invoices needing attention are the recent ones.
    return rows.sort((a, b) => {
      const dateA = a.invoice.billing_period?.start_date || a.invoice.generation_date;
      const dateB = b.invoice.billing_period?.start_date || b.invoice.generation_date;
      return new Date(dateB) - new Date(dateA);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, projectInvoices, searchTerm, selectedFacility, startDate, endDate]);

  const tabCounts = useMemo(() => {
    const counts = { not_invoiced: notInvoicedProjects.length };
    for (const tab of STATUS_TABS) {
      if (tab.key === 'not_invoiced') continue;
      counts[tab.key] = invoiceRows.filter(r => r.bucket === tab.key).length;
    }
    return counts;
  }, [notInvoicedProjects, invoiceRows]);

  const activeRows = useMemo(() => {
    if (activeTab === 'not_invoiced') return notInvoicedProjects;
    return invoiceRows.filter(row => row.bucket === activeTab);
  }, [activeTab, notInvoicedProjects, invoiceRows]);

  const activeTabConfig = STATUS_TABS.find(t => t.key === activeTab) || STATUS_TABS[0];

  // Apply the notification deep link: put the project in the search box and open
  // the tab the invoice is actually in. The `tab` param is only a hint from the
  // event that fired — once the invoices are loaded we re-derive the tab from
  // the invoice's live status, since it has usually moved on since then.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search');
    const tab = params.get('tab');
    const invoiceId = params.get('invoiceId');
    if (!search && !tab && !invoiceId) return;

    const linkKey = `${invoiceId || ''}|${search || ''}|${tab || ''}`;
    const alreadyResolved = deepLinkHandledRef.current === linkKey;
    if (alreadyResolved) return;

    if (search !== null) setSearchTerm(search);
    if (tab && STATUS_TABS.some(t => t.key === tab)) setActiveTab(tab);

    if (!invoiceId) {
      deepLinkHandledRef.current = linkKey;
      return;
    }
    // Wait for the invoices to load before deciding we are done, otherwise the
    // hint tab would stick even when the invoice has since moved on.
    if (loading) return;

    const row = invoiceRows.find(r => String(r.invoice._id) === invoiceId);
    if (row) {
      setActiveTab(row.bucket);
      deepLinkHandledRef.current = linkKey;
      return;
    }
    // Nothing matched: the project may belong to a facility other than the
    // admin's default, which the filter is hiding. Widen it once and let the
    // refetch settle before giving up on resolving the tab.
    if (selectedFacility !== 'all') {
      setSelectedFacility('all');
      return;
    }
    deepLinkHandledRef.current = linkKey;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, loading, invoiceRows, selectedFacility]);

  // Pagination is applied AFTER filtering/search, so search always runs across
  // every loaded project and matching results show regardless of the current
  // page size. Reset to the first page whenever the filtered set or page size
  // changes so we never end up stranded on an out-of-range page.
  const totalPages = Math.max(1, Math.ceil(activeRows.length / rowsPerPage));
  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(0);
    }
  }, [page, totalPages]);
  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedFacility, startDate, endDate, activeTab, rowsPerPage]);

  const startIndex = page * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, activeRows.length);
  const paginatedRows = activeRows.slice(startIndex, endIndex);

  const handleViewPDF = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice._id}/pdf?invoice_id=${invoice.invoice_id}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF');
    }
  };

  // Delete invoice handlers
  const handleDeleteInvoice = async (invoice, project) => {
    try {
      // Get deletion preview first
      const previewResponse = await axios.get(`/api/invoices/deletion-preview/${project.project_id}/${invoice.invoice_id}`);
      
      if (!previewResponse.data.success) {
        toast.error(previewResponse.data.message || 'Failed to get deletion preview');
        return;
      }

      setDeletionPreview(previewResponse.data.preview);
      setInvoiceToDelete({ invoice, project });
      setDeleteConfirmOpen(true);
    } catch (error) {
      console.error('Error getting deletion preview:', error);
      toast.error('Failed to get deletion preview: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    
    try {
      setIsDeleting(true);
      
      const { invoice, project } = invoiceToDelete;
      const response = await axios.delete(`/api/invoices/${project.project_id}/${invoice.invoice_id}`);
      
      if (response.data.success) {
        toast.success('Invoice deleted successfully');
        
        // Refresh the projects and invoices data
        await fetchProjects();
        await fetchInvoices();
        
        // Close the dialog
        setDeleteConfirmOpen(false);
        setInvoiceToDelete(null);
        setDeletionPreview(null);
      } else {
        toast.error(response.data.message || 'Failed to delete invoice');
      }
    } catch (error) {
      console.error('Error deleting invoice:', error);
      toast.error('Failed to delete invoice: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setInvoiceToDelete(null);
    setDeletionPreview(null);
  };

  const handleShowInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetail(true);
  };

  const handleEditInvoice = (invoice, project) => {
    setInvoiceToEdit(invoice);
    setProjectForEdit(project);
    setEditInvoiceDialogOpen(true);
  };

  const handleInvoiceUpdated = (updatedInvoice) => {
    // Refresh data after invoice is updated
    fetchProjects();
    fetchInvoices();
    setEditInvoiceDialogOpen(false);
    setInvoiceToEdit(null);
    setProjectForEdit(null);
  };

  // Lifecycle transitions: finalize (Draft->Billed), revert (Billed->Draft)
  const handleLifecycleTransition = async (invoice, project, action, successMsg) => {
    try {
      const projectRef = project.project_id || project._id;
      await axios.patch(`/api/invoices/${projectRef}/${invoice.invoice_id}/${action}`);
      toast.success(successMsg);
      fetchInvoices();
      fetchProjects();
    } catch (error) {
      const msg = error.response?.data?.message || `Failed to ${action} invoice`;
      toast.error(msg);
    }
  };

  // Finalizing opens a review of the business-office billing request before the
  // invoice is sent to users.
  const handleOpenFinalize = (invoice, project) => {
    setInvoiceToFinalize({ invoice, project });
    setFinalizeDialogOpen(true);
  };

  const handleCloseFinalize = () => {
    setFinalizeDialogOpen(false);
    setInvoiceToFinalize(null);
  };

  const handleFinalized = () => {
    handleCloseFinalize();
    fetchInvoices();
    fetchProjects();
  };

  const handleRevertInvoice = (invoice, project) => {
    const disputeWarning = invoice.workflow_status === 'Disputed'
      ? ' The open dispute will be closed as withdrawn.'
      : '';
    if (!window.confirm(`Revert this invoice to Draft? It will be hidden from users again and any payment split that was started will be cleared.${disputeWarning} This is only allowed if no payer has signed yet.`)) {
      return;
    }
    handleLifecycleTransition(invoice, project, 'revert', 'Invoice reverted to Draft');
  };

  const handleOpenResolveDispute = (invoice, project) => {
    setDisputeToResolve({ invoice, project });
    setResolutionNote('');
    setResolveDisputeOpen(true);
  };

  const handleCloseResolveDispute = () => {
    if (isResolvingDispute) return;
    setResolveDisputeOpen(false);
    setDisputeToResolve(null);
    setResolutionNote('');
  };

  // Re-sending puts the invoice back to Billed so the team can sign it. Any
  // revision the admin needed to make is done beforehand via Edit Invoice.
  // There is no separate "resolved" state — a re-sent invoice is simply Billed.
  const handleResolveDispute = async () => {
    if (!disputeToResolve) return;
    const { invoice, project } = disputeToResolve;
    setIsResolvingDispute(true);
    try {
      const projectRef = project.project_id || project._id;
      await axios.patch(
        `/api/invoices/${projectRef}/${invoice.invoice_id}/dispute/resolve`,
        { resolution_note: resolutionNote }
      );
      toast.success('Invoice re-sent to the project team for signing.');
      setResolveDisputeOpen(false);
      setDisputeToResolve(null);
      setResolutionNote('');
      fetchInvoices();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to re-send the invoice');
    } finally {
      setIsResolvingDispute(false);
    }
  };

  // Count how many designated payers have signed (for progress display).
  const getSigningProgress = (invoice) => {
    const payers = Array.isArray(invoice.payer_distribution) ? invoice.payer_distribution : [];
    if (payers.length === 0) return null;
    const signed = payers.filter(p => p.confirmed).length;
    return { signed, total: payers.length };
  };

  return (
    <div>
        <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 500 }}>
          Project Invoicing
        </Typography>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '24px' 
        }}>
          <Typography variant="h5">Invoices</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<BatchPredictionIcon />}
              onClick={() => setShowBatchInvoice(true)}
            >
              Batch Invoice
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search Projects"
                placeholder="Search by ID, title, or PI name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Facility</InputLabel>
                <Select
                  value={selectedFacility}
                  onChange={(e) => setSelectedFacility(e.target.value)}
                  label="Facility"
                >
                  <MenuItem value="all">All Facilities</MenuItem>
                  {facilities.map(facility => (
                    <MenuItem key={facility._id} value={facility._id}>
                      {facility.facility_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                value={startDate ? startDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                value={endDate ? endDate.toISOString().split('T')[0] : ''}
                onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Lifecycle tabs: one bucket per stage, so an invoice is only ever
            listed under a single tab. The explicit colours are load-bearing —
            a global `button` rule paints every bare button in the brand red,
            which left the tab strip unreadable. */}
        <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(e, value) => setActiveTab(value)}
            variant="fullWidth"
            TabIndicatorProps={{ sx: { height: 3, backgroundColor: BRAND_RED } }}
            sx={{
              backgroundColor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0',
              '& .MuiTab-root': {
                textTransform: 'none',
                minHeight: 56,
                fontWeight: 600,
                fontSize: '0.88rem',
                backgroundColor: 'transparent',
                color: '#475569',
                width: 'auto',
                '&:hover': { backgroundColor: '#f1f5f9', color: '#1e293b' },
                '&.Mui-selected': { backgroundColor: '#fff', color: BRAND_RED }
              }
            }}
          >
            {STATUS_TABS.map(tab => {
              const isActive = activeTab === tab.key;
              const isOpenDispute = tab.key === 'dispute' && (tabCounts.dispute ?? 0) > 0;
              return (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{tab.label}</span>
                      <Box
                        component="span"
                        sx={{
                          minWidth: 24,
                          px: 0.75,
                          py: '2px',
                          borderRadius: '10px',
                          fontSize: '0.7rem',
                          fontWeight: 700,
                          lineHeight: 1.4,
                          backgroundColor: isOpenDispute
                            ? BRAND_RED
                            : isActive ? 'rgba(196, 30, 58, 0.12)' : '#e2e8f0',
                          color: isOpenDispute
                            ? '#fff'
                            : isActive ? BRAND_RED : '#475569'
                        }}
                      >
                        {tabCounts[tab.key] ?? 0}
                      </Box>
                    </Box>
                  }
                />
              );
            })}
          </Tabs>
          <Typography variant="body2" color="text.secondary" sx={{ px: 3, py: 1.5 }}>
            {activeTabConfig.help}
          </Typography>
        </Paper>

        {/* Active tab contents */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {loading || prefLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 6 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading projects and invoices...
              </Typography>
            </Box>
          ) : error ? (
            <Paper sx={{ p: 3, textAlign: 'center', color: 'error.main' }}>
              <Typography>{error}</Typography>
            </Paper>
          ) : activeRows.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {activeTab === 'not_invoiced'
                  ? 'No project matching these filters has uninvoiced compartment usage.'
                  : `No invoices in ${activeTabConfig.label} match these filters.`}
              </Typography>
            </Paper>
          ) : activeTab === 'not_invoiced' ? (
            paginatedRows.map(({ project, unbilled }) => {
              const displayUser = getDisplayUser(project);
              const existingInvoices = (projectInvoices[project._id] || []).length;
              return (
                <Card key={project._id} variant="outlined" sx={INVOICE_CARD_SX}>
                  <CardContent>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {project.project_id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {project.project_title}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2">
                          <strong>PI:</strong> {displayUser ? `${displayUser.first_name} ${displayUser.last_name}` : 'N/A'}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Facility:</strong> {project.facility?.facility_name || 'N/A'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="body2">
                          <strong>Unbilled:</strong>{' '}
                          <Box component="span" sx={{ color: BRAND_RED, fontWeight: 600 }}>
                            {unbilled.unbilled_days} compartment {unbilled.unbilled_days === 1 ? 'day' : 'days'}
                          </Box>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(unbilled.earliest_unbilled_start)} - {formatDate(unbilled.latest_unbilled_end)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {existingInvoices === 0
                            ? 'Never invoiced'
                            : `${existingInvoices} earlier ${existingInvoices === 1 ? 'invoice' : 'invoices'}`}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={2} sx={{ textAlign: { md: 'right' } }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => handleProjectSelect(project)}
                        >
                          Generate Invoice
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            paginatedRows.map(({ invoice, project }, rowIndex) => {
              const displayUser = getDisplayUser(project);
              return (
                          <Card
                            key={`${project._id}-${invoice.invoice_id}-${rowIndex}`}
                            variant="outlined"
                            sx={INVOICE_CARD_SX}
                          >
                            {/* Project header: rows are no longer nested under a
                                project, so each one names its own project. The tinted
                                band also marks where one invoice card starts. */}
                            <Box sx={CARD_HEADER_SX}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {project.project_id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {project.project_title}
                              </Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              <Typography variant="caption" color="text.secondary">
                                PI: {displayUser ? `${displayUser.first_name} ${displayUser.last_name}` : 'N/A'}
                                {project.facility?.facility_name ? ` • ${project.facility.facility_name}` : ''}
                              </Typography>
                            </Box>
                            <CardContent>
                              <Grid container spacing={2} alignItems="flex-start">
                                {/* First Column: Invoice #, Period, Generated */}
                                <Grid item xs={12} md={3}>
                                  <Typography variant="subtitle1">
                                    Invoice #{getInvoiceNumber(invoice.invoice_id)}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Period: {invoice.billing_period ? 
                                      `${formatDate(invoice.billing_period.start_date)} - ${formatDate(invoice.billing_period.end_date)}` 
                                      : 'N/A'}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Generated: {formatDate(invoice.generation_date)}
                                  </Typography>
                                  {invoice.billed_by && (
                                    <Typography variant="body2" color="text.secondary">
                                      Billed by: {invoice.billed_by.first_name} {invoice.billed_by.last_name}
                                    </Typography>
                                  )}
                                </Grid>
                                {/* Second Column: Amount, Status */}
                                <Grid item xs={12} md={2}>
                                  <Typography variant="body2">
                                    <strong>Amount:</strong> ${(invoice.current_period_amount || invoice.total_amount)?.toFixed(2)}
                                  </Typography>
                                  <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <strong>Status:</strong>{' '}
                                    <Chip
                                      label={getInvoiceStatus(invoice)}
                                      size="small"
                                      color={getStatusColor(getInvoiceStatus(invoice))}
                                    />
                                    {invoice.is_legacy && (
                                      <Chip
                                        label="Legacy"
                                        size="small"
                                        variant="outlined"
                                        color="secondary"
                                        title="Created under the previous invoice system"
                                      />
                                    )}
                                  </Typography>
                                  {(invoice.amount_paid || 0) > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      Paid ${(invoice.amount_paid || 0).toFixed(2)} · Balance $
                                      {(invoice.remaining_balance ?? ((invoice.total_amount || 0) - (invoice.amount_paid || 0))).toFixed(2)}
                                    </Typography>
                                  )}
                                  {(() => {
                                    const progress = getSigningProgress(invoice);
                                    const status = getInvoiceStatus(invoice);
                                    if (progress && ['Billed', 'Disputed', 'Signed'].includes(status)) {
                                      return (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                          Signed: {progress.signed}/{progress.total} payers
                                        </Typography>
                                      );
                                    }
                                    return null;
                                  })()}
                                </Grid>
                                {/* Third Column: A/R Mapping */}
                                <Grid item xs={12} md={3}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {(() => {
                                      const mappingInfo = getAccountMappingInfo(invoice);
                                      if (mappingInfo) {
                                        return (
                                          <>
                                            <LinkIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                            <Typography variant="body2" sx={{ color: 'success.main' }}>
                                              <strong>Accounts Receivable:</strong> {mappingInfo.facilityName} - {mappingInfo.accountNumber}
                                            </Typography>
                                            <IconButton aria-label="Edit A/R Mapping"
                                              size="small"
                                              sx={{ p: 0.25, ml: 0.5 }}
                                              onClick={() => handleOpenAccountMapping(invoice, project)}
                                              title="Edit A/R Mapping"
                                            >
                                              <EditIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                          </>
                                        );
                                      } else {
                                        return (
                                          <>
                                            <LinkOffIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                            <Typography variant="body2" sx={{ color: 'warning.main' }}>
                                              <strong>Accounts Receivable:</strong> Not mapped
                                            </Typography>
                                            <Button
                                              size="small"
                                              variant="text"
                                              sx={{ minWidth: 'auto', p: 0.25, fontSize: '0.7rem', ml: 0.5 }}
                                              onClick={() => handleOpenAccountMapping(invoice, project)}
                                            >
                                              Map
                                            </Button>
                                          </>
                                        );
                                      }
                                    })()}
                                  </Box>
                                </Grid>
                                {/* Fourth Column: Action Buttons */}
                                <Grid item xs={12} md={4}>
                                  <Stack
                                    direction="row"
                                    spacing={0.75}
                                    justifyContent="flex-end"
                                    flexWrap="wrap"
                                    useFlexGap
                                    sx={{ rowGap: 0.75 }}
                                  >
                                    {(() => {
                                      const status = getInvoiceStatus(invoice);
                                      const isLegacy = !!invoice.is_legacy;
                                      const isDraft = !isLegacy && (status === 'Draft' || status === 'Unknown');
                                      const isFinal = !isLegacy && status === 'Final';
                                      const isBilled = !isLegacy && status === 'Billed';
                                      const isDisputed = !isLegacy && status === 'Disputed';
                                      return (
                                        <>
                                          {/* Legacy invoices keep the old edit capability; new-flow
                                              invoices are editable while Draft or Billed, and while
                                              Disputed because revising is how a dispute gets fixed. */}
                                          {(isLegacy || isDraft || isBilled || isDisputed) && (
                                            <Button
                                              size="small"
                                              variant={isDisputed ? 'contained' : 'outlined'}
                                              onClick={() => handleEditInvoice(invoice, project)}
                                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                            >
                                              Edit Invoice
                                            </Button>
                                          )}
                                          {isDisputed && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="success"
                                              onClick={() => handleOpenResolveDispute(invoice, project)}
                                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                            >
                                              Re-send for Signing
                                            </Button>
                                          )}
                                          {(isDraft || isFinal) && (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="primary"
                                              onClick={() => handleOpenFinalize(invoice, project)}
                                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                            >
                                              Finalize &amp; Send
                                            </Button>
                                          )}
                                          {(isBilled || isDisputed) && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="warning"
                                              onClick={() => handleRevertInvoice(invoice, project)}
                                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                            >
                                              Revert to Draft
                                            </Button>
                                          )}
                                        </>
                                      );
                                    })()}
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleViewPDF(invoice)}
                                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                    >
                                      View PDF
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => handleDownloadPDF(invoice._id, invoice.invoice_id)}
                                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                    >
                                      Download PDF
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="success"
                                      onClick={() => handleOpenPayments(invoice)}
                                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                    >
                                      Payments{invoice.payments?.length > 0 ? ` (${invoice.payments.length})` : ''}
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      onClick={() => handleDeleteInvoice(invoice, project)}
                                      sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                    >
                                      Delete
                                    </Button>
                                  </Stack>
                                </Grid>
                              </Grid>

                              {/* What the PI actually objected to, so the admin can
                                  act on it without opening the project chat. */}
                              {invoice.dispute?.is_open && (
                                <Alert severity="error" sx={{ mt: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Disputed{invoice.dispute.raised_at ? ` on ${formatDate(invoice.dispute.raised_at)}` : ''}
                                  </Typography>
                                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {invoice.dispute.reason || 'No reason given.'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Revise the invoice if needed, then use Re-send for Signing to put it back in front of the team.
                                  </Typography>
                                </Alert>
                              )}
                            </CardContent>
                          </Card>
              );
            })
          )}
        </Box>

        {/* Pagination Controls */}
        {!loading && !error && activeRows.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1 }}>Show</Typography>
              <Select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
                size="small"
                sx={{ mx: 1 }}
              >
                {[5, 10, 20, 50].map((n) => (
                  <MenuItem key={n} value={n}>{n}</MenuItem>
                ))}
              </Select>
              <Typography variant="body2" sx={{ ml: 1 }}>{activeTabConfig.unit} per page</Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton aria-label="First page" onClick={() => setPage(0)} disabled={page === 0} size="small">
                <FirstPageIcon />
              </IconButton>
              <IconButton aria-label="Previous page" onClick={() => setPage(page - 1)} disabled={page === 0} size="small">
                <KeyboardArrowLeftIcon />
              </IconButton>
              <Typography variant="body2" sx={{ mx: 2 }}>
                Page {page + 1} of {totalPages}
              </Typography>
              <IconButton aria-label="Next page" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} size="small">
                <KeyboardArrowRightIcon />
              </IconButton>
              <IconButton aria-label="Last page" onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} size="small">
                <LastPageIcon />
              </IconButton>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Showing {activeRows.length > 0 ? startIndex + 1 : 0} to {endIndex} of {activeRows.length} {activeTabConfig.unit}
            </Typography>
          </Box>
        )}

        {/* Generate Invoice Dialog */}
        <GenerateNewInvoice
          open={generateInvoiceDialogOpen}
          onClose={() => {
            setGenerateInvoiceDialogOpen(false);
            setSelectedProject(null);
          }}
          selectedProject={selectedProject}
          onInvoiceCreated={handleInvoiceGenerated}
          setSelectedProject={setSelectedProject}
        />

        {/* View Invoice Details Dialog */}
        {showInvoiceDetail && selectedInvoice && (
          <InvoiceDetail 
            open={showInvoiceDetail}
            onClose={() => setShowInvoiceDetail(false)}
            invoiceId={selectedInvoice._id || selectedInvoice.parentId}
            selectedInvoice={selectedInvoice}
            onUpdate={handlePaymentRecorded}
          />
        )}

        {/* Unified Payments Dialog */}
        <Dialog
          open={paymentsDialogOpen}
          onClose={() => {
            setPaymentsDialogOpen(false);
            setPaymentsInvoice(null);
          }}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">
                Payments — Invoice #{getInvoiceNumber(paymentsInvoice?.invoice_id)}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Total: <strong>${paymentsInvoice?.total_amount?.toFixed(2) || '0.00'}</strong>
                  {' | '}Paid: <strong>${paymentsInvoice?.amount_paid?.toFixed(2) || '0.00'}</strong>
                  {' | '}Balance: <strong>${paymentsInvoice?.remaining_balance?.toFixed(2) ||
                    ((paymentsInvoice?.total_amount || 0) - (paymentsInvoice?.amount_paid || 0)).toFixed(2)}</strong>
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {paymentsInvoice?.payments?.length > 0 ? (
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Paid By</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Receipt / Txn ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paymentsInvoice.payments.map((payment, idx) => {
                      const paidByName = typeof payment.paid_by === 'object' && payment.paid_by
                        ? `${payment.paid_by.first_name || ''} ${payment.paid_by.last_name || ''}`.trim() || 'N/A'
                        : 'N/A';
                      return (
                        <TableRow key={payment._id || idx} hover>
                          <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                          <TableCell>{paidByName}</TableCell>
                          <TableCell>${payment.amount?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{payment.payment_mode}</TableCell>
                          <TableCell>{payment.receipt_number || payment.transaction_id || '—'}</TableCell>
                          <TableCell>{payment.comments || '—'}</TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => {
                                  setPaymentToEdit(payment);
                                  setEditPaymentDialogOpen(true);
                                }}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                color="error"
                                onClick={() => {
                                  setPaymentToDelete(payment);
                                  setDeletePaymentConfirmOpen(true);
                                }}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No payments recorded yet.</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setPaymentsDialogOpen(false);
                setPaymentsInvoice(null);
              }}
            >
              Close
            </Button>
            {paymentsInvoice && (paymentsInvoice.remaining_balance > 0 ||
              ((paymentsInvoice.total_amount || 0) - (paymentsInvoice.amount_paid || 0)) > 0) && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setShowRecordPayment(true)}
                sx={{ textTransform: 'none' }}
              >
                Record New Payment
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Record Payment Dialog (opened from Payments dialog) */}
        {paymentsInvoice && (
          <RecordPayment
            open={showRecordPayment}
            onClose={() => setShowRecordPayment(false)}
            invoice={paymentsInvoice}
            onPaymentRecorded={handlePaymentRecorded}
          />
        )}

        {/* Edit Payment Dialog (opened from Payments dialog) */}
        {editPaymentDialogOpen && paymentsInvoice && paymentToEdit && (
          <EditPayment
            open={editPaymentDialogOpen}
            onClose={() => {
              setEditPaymentDialogOpen(false);
              setPaymentToEdit(null);
            }}
            invoice={paymentsInvoice}
            payment={paymentToEdit}
            onPaymentUpdated={handlePaymentUpdated}
          />
        )}

        {/* Delete Payment Confirmation Dialog */}
        <Dialog
          open={deletePaymentConfirmOpen}
          onClose={() => {
            if (!isDeletingPayment) {
              setDeletePaymentConfirmOpen(false);
              setPaymentToDelete(null);
            }
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Delete Payment?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              This will permanently remove the payment of{' '}
              <strong>${paymentToDelete?.amount?.toFixed(2) || '0.00'}</strong>
              {paymentToDelete?.payment_mode ? ` (${paymentToDelete.payment_mode})` : ''} from
              Invoice #{getInvoiceNumber(paymentsInvoice?.invoice_id)}.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
              The invoice balance and the User Accounts Report will be updated accordingly. This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={() => {
                setDeletePaymentConfirmOpen(false);
                setPaymentToDelete(null);
              }}
              disabled={isDeletingPayment}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDeletePayment}
              disabled={isDeletingPayment}
              startIcon={isDeletingPayment ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            >
              {isDeletingPayment ? 'Deleting...' : 'Delete Payment'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Finalize & Send: review the billing request, then send to users */}
        {finalizeDialogOpen && invoiceToFinalize && (
          <FinalizeInvoiceDialog
            open={finalizeDialogOpen}
            invoice={invoiceToFinalize.invoice}
            project={invoiceToFinalize.project}
            invoiceNumber={getInvoiceNumber(invoiceToFinalize.invoice?.invoice_id)}
            onClose={handleCloseFinalize}
            onFinalized={handleFinalized}
          />
        )}

        {/* Re-send a disputed invoice. It goes straight back to Billed — there is
            deliberately no "resolved" state in between. */}
        <Dialog
          open={resolveDisputeOpen}
          onClose={handleCloseResolveDispute}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Re-send Invoice for Signing
            {disputeToResolve && (
              <Typography variant="body2" color="text.secondary">
                Invoice #{getInvoiceNumber(disputeToResolve.invoice?.invoice_id)} — {disputeToResolve.project?.project_id}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent dividers>
            {disputeToResolve?.invoice?.dispute?.reason && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>What the project team raised</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {disputeToResolve.invoice.dispute.reason}
                </Typography>
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This puts the invoice back in front of the project team for signing and returns it to
              <strong> Billed</strong>. If the amounts need to change, close this and use{' '}
              <strong>Edit Invoice</strong> first — your edits are saved to the same invoice.
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Response to the project team (optional)"
              placeholder="e.g. Removed the duplicate compartment charge for March."
              value={resolutionNote}
              onChange={(e) => setResolutionNote(e.target.value)}
              inputProps={{ maxLength: 2000 }}
              helperText="Shown in the project activity log and in the notification the team receives."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleCloseResolveDispute} disabled={isResolvingDispute}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleResolveDispute}
              disabled={isResolvingDispute}
              startIcon={isResolvingDispute ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isResolvingDispute ? 'Sending...' : 'Re-send for Signing'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Batch Invoice Dialog */}
        <BatchInvoicing
          open={showBatchInvoice}
          onClose={() => {
            setShowBatchInvoice(false);
            fetchInvoices();
          }}
        />

        {/* Edit Invoice Dialog */}
        {editInvoiceDialogOpen && invoiceToEdit && projectForEdit && (
          <EditInvoice
            open={editInvoiceDialogOpen}
            onClose={() => {
              setEditInvoiceDialogOpen(false);
              setInvoiceToEdit(null);
              setProjectForEdit(null);
            }}
            invoice={invoiceToEdit}
            project={projectForEdit}
            onInvoiceUpdated={handleInvoiceUpdated}
          />
        )}

        {/* Delete Invoice Confirmation Dialog */}
        <Dialog 
          open={deleteConfirmOpen} 
          onClose={handleCancelDelete}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Typography variant="h6" color="error">
              Delete Invoice Confirmation
            </Typography>
          </DialogTitle>
          <DialogContent>
            {deletionPreview && (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Are you sure you want to delete invoice <strong>{deletionPreview.invoiceId}</strong>?
                </Typography>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#664d03', fontWeight: 600 }} gutterBottom>
                    ⚠️ Warning: This action cannot be undone!
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#664d03' }}>
                    Deleting this invoice will rollback all billing periods and invoiced quantities associated with it.
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Invoice Details:
                  </Typography>
                  <Typography variant="body2">
                    • Invoice Date: {new Date(deletionPreview.invoiceDate).toLocaleDateString()}
                  </Typography>
                  <Typography variant="body2">
                    • Total Amount: ${deletionPreview.totalAmount?.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    • Amount Paid: ${deletionPreview.amountPaid?.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    • Remaining Balance: ${deletionPreview.remainingBalance?.toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    What will be affected:
                  </Typography>
                  <Typography variant="body2">
                    • Compartment billing periods to be removed: <strong>{deletionPreview.affectedChamberPeriods}</strong>
                  </Typography>
                  <Typography variant="body2">
                    • Resource invoiced quantities to be removed: <strong>{deletionPreview.affectedResourceQuantities}</strong>
                  </Typography>
                </Box>

                {deletionPreview.hasPayments && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: '#664d03' }}>
                      <strong>⚠️ Warning:</strong> This invoice has recorded payments totaling ${deletionPreview.amountPaid?.toFixed(2)}.
                      The payments will be permanently deleted along with the invoice.
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={handleCancelDelete} 
              disabled={isDeleting}
            >
              Cancel
            </Button>
            {deletionPreview && (
              <Button 
                onClick={handleConfirmDelete} 
                color="error" 
                variant="contained"
                disabled={isDeleting}
                startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
              >
                {isDeleting ? 'Deleting...' : 'Delete Invoice'}
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Account Mapping Dialog */}
        <Dialog 
          open={accountMappingDialogOpen} 
          onClose={() => setAccountMappingDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Map Invoice to Account Receivable
            {invoiceForMapping && (
              <Typography variant="body2" color="text.secondary">
                Invoice #{getInvoiceNumber(invoiceForMapping?.invoice_id)}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Facility</InputLabel>
                <Select
                  value={selectedMappingFacility}
                  onChange={(e) => handleMappingFacilityChange(e.target.value)}
                  label="Facility"
                >
                  <MenuItem value="">
                    <em>Select a facility</em>
                  </MenuItem>
                  {facilities.map((facility) => (
                    <MenuItem key={facility._id} value={facility._id}>
                      {facility.facility_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedFacilityAccounts.length > 0 && (
                <FormControl fullWidth>
                  <InputLabel>Account Receivable</InputLabel>
                  <Select
                    value={selectedAccountNumber}
                    onChange={(e) => setSelectedAccountNumber(e.target.value)}
                    label="Account Receivable"
                  >
                    <MenuItem value="">
                      <em>Select an account</em>
                    </MenuItem>
                    {selectedFacilityAccounts.map((account, index) => (
                      <MenuItem key={index} value={account.account_number}>
                        {account.account_number} {account.description && `- ${account.description}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {selectedMappingFacility && selectedFacilityAccounts.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No accounts receivable configured for this facility.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAccountMappingDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSaveAccountMapping} 
              variant="contained" 
              color="primary"
              disabled={!selectedMappingFacility || !selectedAccountNumber}
            >
              Save Mapping
            </Button>
          </DialogActions>
        </Dialog>
    </div>
  );
};

export default Invoicing;
